import { Component, OnInit } from '@angular/core';

import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ReactiveFormsModule } from '@angular/forms';
import { first, mergeMap, Observable, tap } from 'rxjs';
import { PurposeEnum } from 'src/app/models/shared-models';
import { FeatureFlags } from 'src/app/services/feature-flag.service';
import { BaseWizardComponent } from '../base-wizard.component';
import { ILotPlanModel, IGeneralInformationModel, IPurposeModel, IPurposeDataModel } from '../models/veg-wizard-models';
import { PurposeService } from '../services/purpose-service';
import { UtilService } from '../services/util.service';
import { VegWizardDataService } from '../services/veg-wizard-data.service';

@UntilDestroy()
@Component({
  selector: 'app-select-purpose',
  templateUrl: './select-purpose.component.html',
  styleUrls: ['./select-purpose.component.css'],
  imports: [
    ReactiveFormsModule
],
})
export class SelectPurposeComponent extends BaseWizardComponent implements OnInit {

  form: FormGroup;
  lotOnPlan: ILotPlanModel | undefined;
  nextButtonClicked = false;
  nextSecondButtonClicked = false;

  get purposeCodeControl() { return this.form.controls['purposeCode']; }
  get isPurposeMessageAnswerValidControl() { return this.form.controls['isPurposeMessageAnswerValid']; }
  get isSecondPurposeMessageAnswerValidControl() { return this.form.controls['isSecondPurposeMessageAnswerValid']; }

  isNFPPurposeValid: boolean | null = null;
  isNFPPurposeSelected: boolean | null = null;
  isSecondPurposeMessageAnswerValid: boolean | null = null;

  purposeQuestionaire: IGeneralInformationModel = {
    promptQuestionMessage: '',
    userSelectsNoDisplayMessage: '',
    userSelectsYesDisplayMessage: '',
    generalInfoMessage: '',
    secondPromptQuestionMessage: '',
    secondUserSelectsNoDisplayMessage: '',
    secondUserSelectsYesDisplayMessage: '',
  };

  purposeRefData: IPurposeModel[] = [];
  purposeData: IPurposeDataModel | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private vwds: VegWizardDataService,
    private purposeService: PurposeService,
    private utilService: UtilService,
    router: Router) {
    super();

    this.lotOnPlan = this.vwds.getLotPlanModel();
    this.form = this.formBuilder.group({}); // ensure form its reset
  }

  ngOnInit(): void {
    // grab any existing purpose data we may have.
    this.purposeData = this.vwds.getPurpose();

    // set our form with new or previous values we've retrieved from getPurpose.
    // any data we set here on form init, will be treated as the pristine state. I.e. if
    // we reset the form, we'll return to this state.
    if(this.featureFlag(FeatureFlags.NFP2019)) {
      this.form = this.formBuilder.group({
        purposeCode: [this.purposeData?.purpose?.purposeCode, Validators.required],
        isPurposeMessageAnswerValid: [this.purposeData?.purposeMessageAnswerValid ?? undefined],
        isSecondPurposeMessageAnswerValid: [this.isSecondPurposeMessageAnswerValid ?? null]
      });
    }else {
      this.form = this.formBuilder.group({
        purposeCode: [this.purposeData?.purpose?.purposeCode, Validators.required],
        isPurposeMessageAnswerValid: [this.purposeData?.purposeMessageAnswerValid ?? undefined]
      });
    }

    if (this.purposeData?.purposeMessageAnswerValid !== undefined) {
      this.isPurposeMessageAnswerValidControl.setValidators([Validators.requiredTrue]);
    }

    if (!!this.purposeData?.purposeMessageAnswerValid && !!this.purposeData?.purpose?.purposeCode) {
      this.purposeQuestionaire = this.purposeService.getPurposeMessage(this.purposeData?.purpose?.purposeCode);
    }

    if(this.featureFlag(FeatureFlags.NFP2019)) {
      if (this.purposeData?.purpose?.purposeCode === PurposeEnum.nativeForestPractice) {
        this.form.controls['isPurposeMessageAnswerValid'].setValue(false);
        this.isPurposeMessageAnswerValidControl.setErrors(null);
        this.isNFPPurposeValid = this.purposeData.purposeMessageAnswerValid;
        this.form.controls['isSecondPurposeMessageAnswerValid'].setValue(true);
      }
    }

    this.setPurposeData$()
      .pipe(first(), untilDestroyed(this))
      .subscribe(() => {
        this.initForm();
      });
  }

  private initForm() {

    if(this.featureFlag(FeatureFlags.NFP2019)) {
      this.purposeCodeControl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((purposeCode: string) => {
          this.purposeQuestionaire = this.purposeService.getPurposeMessage(purposeCode);
          // based on the purpose questionaire selection, add or remove validators.
          this.performValidation();
          // clear out our isPurposeMessageAnswerValid selection when the user changes their mind on purpose.
          this.isPurposeMessageAnswerValidControl.setValue(undefined);
          this.isSecondPurposeMessageAnswerValidControl.setValue(undefined);
        });

      this.isSecondPurposeMessageAnswerValidControl?.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.performValidation();
        });
    }else{
      this.purposeCodeControl.valueChanges
        .pipe(untilDestroyed(this))
        .subscribe((purposeCode: string) => {
          this.purposeQuestionaire = this.purposeService.getPurposeMessage(purposeCode);
          // based on the purpose questionaire selection, add or remove validators.
          this.performValidation();
          // clear out our isPurposeMessageAnswerValid selection when the user changes their mind on purpose.
          this.isPurposeMessageAnswerValidControl.setValue(undefined);
        });
    }

    this.isPurposeMessageAnswerValidControl.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.performValidation();
      });

    this.isSecondPurposeMessageAnswerValidControl?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.performValidation();
      });
  }

  private performValidation() {
    this.isPurposeMessageAnswerValidControl.setErrors(null);

    if(this.featureFlag(FeatureFlags.NFP2019)){
      this.isNFPPurposeValid = null;
      const formModel = this.form.getRawValue();
      const purpose = this.getPurposeByCode(formModel.purposeCode);
      this.isNFPPurposeSelected = purpose?.purposeCode === PurposeEnum.nativeForestPractice;
      const isSecondPurposeMessageAnswerValid = this.isSecondPurposeMessageAnswerValidControl.value;
      const isPurposeMessageAnswerValid = this.isPurposeMessageAnswerValidControl.value;

      if (purpose?.purposeCode === PurposeEnum.nativeForestPractice
        && this.nextButtonClicked && isPurposeMessageAnswerValid === undefined) {
        this.isPurposeMessageAnswerValidControl.setErrors({ 'Required': true });
      }

      if (this.purposeQuestionaire && this.purposeQuestionaire?.promptQuestionMessage) {
        if (this.purposeCodeControl.value === PurposeEnum.nativeForestPractice) {

          // for NFP the validation is inverted, it should block the user when selection is true
          if (isPurposeMessageAnswerValid === true) {
            this.isPurposeMessageAnswerValidControl.setErrors({ 'Required': true });
          }

          if (isSecondPurposeMessageAnswerValid === false) {
            this.isSecondPurposeMessageAnswerValidControl.setErrors({ 'Required': true });
          }
          else if (isSecondPurposeMessageAnswerValid === true) {
            this.isSecondPurposeMessageAnswerValidControl.setErrors(null);
            this.isNFPPurposeValid = (isPurposeMessageAnswerValid === false && isSecondPurposeMessageAnswerValid === true);
          } else if (isSecondPurposeMessageAnswerValid === undefined && isPurposeMessageAnswerValid === false) {
            if (!this.isSecondPurposeMessageAnswerValidControl.dirty) {
              this.isSecondPurposeMessageAnswerValidControl.markAsDirty();
              this.form.controls['isSecondPurposeMessageAnswerValid'].setValue(null);
            }
          }
        }
        else {
          if (isPurposeMessageAnswerValid === true) {
            this.isPurposeMessageAnswerValidControl.setErrors(null);
          }
          else {
            this.isPurposeMessageAnswerValidControl.setErrors({ 'Required': true });
          }
        }
      }
    }else{
      if (this.purposeQuestionaire && this.purposeQuestionaire?.promptQuestionMessage){
        if (this.isPurposeMessageAnswerValidControl.value === true) {
          this.isPurposeMessageAnswerValidControl.setErrors(null);
        }
        else {
          this.isPurposeMessageAnswerValidControl.setErrors({ 'Required': true });
        }
      }
    }
  }

  private setPurposeData$(): Observable<IPurposeModel[]> {
    return this.purposeService.getPurposes$()
      .pipe(
        tap((res) => {
          this.purposeRefData = res;
        })
      );
  }

  private getPurposeByCode(code: string) {
    if (!code || !code.trim()) { return undefined; }

    return this.purposeRefData
      .find(p => p?.purposeCode?.trim()?.toLowerCase() === code.trim().toLowerCase())
  }

  override next(): void {
    this.form.markAsDirty();
    this.nextButtonClicked = true;
    this.performValidation();

    const formModel = this.form.getRawValue();
    const purpose = this.getPurposeByCode(formModel.purposeCode);


    if (this.form.invalid) {
      this.utilService.getFormValidationErrors(this.form);
      return;
    }

    const model: IPurposeDataModel = {
      purposeMessageAnswerValid: formModel.isPurposeMessageAnswerValid,
      purpose: purpose,
    } as IPurposeDataModel;

    if(this.featureFlag(FeatureFlags.NFP2019)) {
      if (this.isNFPPurposeSelected && this.isPurposeMessageAnswerValidControl.value === false) {
        this.nextSecondButtonClicked = true;
      }

      if (this.isNFPPurposeSelected && !this.isNFPPurposeValid) {
        return;
      }

      if (purpose?.purposeCode === PurposeEnum.nativeForestPractice) {
        model.purposeMessageAnswerValid = true;
      }
    }

    this.vwds.setPurpose$(model)
      .pipe(
        first(),
        untilDestroyed(this),
        mergeMap(() => this.vwds.setSelfAuditRequired$())
      )
      .subscribe(() => {
        super.next();
      });
  }

  override cancel(): void {
    super.cancel();
    this.vwds.clear().pipe(first(), untilDestroyed(this)).subscribe();
  }
}
